import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useAnnouncements } from '../hooks';
import { announcementAudienceLabel } from '../lib/announcementDelivery';
import styles from './Announcements.module.css';

const Announcements: React.FC = () => {
  const [search, setSearch] = useState('');
  const { data, isLoading, isError, refetch } = useAnnouncements({
    limit: 200,
  });
  const announcements = (data?.data ?? []).filter((announcement) =>
    `${announcement.title} ${announcement.message} ${announcement.authorName}`
      .toLocaleLowerCase()
      .includes(search.trim().toLocaleLowerCase()),
  );
  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1>Announcements</h1>
          <p>
            Sent announcements and delivery records. Send a new announcement to
            correct or follow up on a previous message.
          </p>
        </div>
        <Link className={styles.createBtn} to="/messages/broadcast">
          New announcement
        </Link>
      </header>
      <div className={styles.filters}>
        <label htmlFor="announcement-search">Search sent announcements</label>
        <input
          id="announcement-search"
          type="search"
          className={styles.select}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {isLoading ? (
        <p className={styles.loading} role="status">
          Loading announcements…
        </p>
      ) : isError ? (
        <div className={styles.empty} role="alert">
          <p>Couldn&apos;t load announcement delivery records.</p>
          <button
            type="button"
            className={styles.createBtn}
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      ) : !announcements.length ? (
        <div className={styles.empty}>
          <p>
            {search
              ? 'No matching announcements.'
              : 'No sent announcements yet.'}
          </p>
          <Link to="/messages/broadcast">Compose an announcement</Link>
        </div>
      ) : (
        <div className={styles.announcementsList}>
          {announcements.map((announcement) => (
            <article
              key={announcement.id}
              className={`${styles.announcementCard} ${styles.info}`}
            >
              <header className={styles.cardHeader}>
                <div className={styles.headerContent}>
                  <h2>{announcement.title}</h2>
                  <p className={styles.meta}>
                    By {announcement.authorName} ·{' '}
                    {Number.isFinite(new Date(announcement.createdAt).getTime())
                      ? formatDistanceToNow(new Date(announcement.createdAt), {
                          addSuffix: true,
                        })
                      : 'Date unavailable'}
                  </p>
                </div>
              </header>
              <div className={styles.cardBody}>
                <p
                  className={styles.message}
                  style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                >
                  {announcement.message}
                </p>
              </div>
              <footer className={styles.cardFooter}>
                <span className={styles.targetBadge}>
                  {announcementAudienceLabel(announcement)}
                </span>
                <span className={styles.readStats}>
                  {announcement.readCount} / {announcement.totalRecipients} read
                </span>
              </footer>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default Announcements;
